import React, { useEffect, useRef, useState } from 'react';
import { Form } from 'react-final-form';
import { useMutation } from '@apollo/client';
import { CheckboxField, TextAreaField, TextField, SelectField } from 'components/common/Forms';
import { required } from 'components/common/Forms/Validators';
import { Footer } from 'components/common/Modal';
import { UPDATE_SLIDE_SHOW } from 'components/admin/SlideShow/Utils/slideShowGraphQL';
import useSlideShowContext from '@/components/admin/SlideShow/stores/useSlideShowContext';
import Popover from '@/components/ui/Popover';
import { admin_themes_path } from '@/modules/routes';
import Icon from '@/components/ui/Icon';
import I18n from 'i18n-js';
import styles from './SlideShowTitle.module.scss';

interface SlideShowTitleFormProps {
  isSaving: boolean;
  setIsSaving: (saving) => void;
  slideShow: {
    id: number;
    titleEn?: string;
    titleEs?: string;
    descriptionEn?: string;
    descriptionEs?: string;
    activityType?: string;
    showTransitionSlides?: boolean;
    themeId?: number;
  }
}

const SlideShowTitleForm = ({ isSaving, setIsSaving, slideShow }: SlideShowTitleFormProps) => {
  const updateSlideShowInStore = useSlideShowContext(state => state.updateSlideShow);
  const userType = useSlideShowContext(state => state.userType);
  const isAdmin = useSlideShowContext(state => state.isAdmin);
  const setThemeIdStore = useSlideShowContext(state => state.setThemeId);
  const storeThemeId = useSlideShowContext(state => state.themeId);
  const themes = useSlideShowContext(state => state.themes);

  const [saved, setSaved] = useState(false);
  // Using a ref here to accurately track the saved state in the useEffect cleanup. Otherwise the state is always false.
  const savedRef = useRef(null);
  savedRef.current = saved;

  const [updateSlideShowMutation] = useMutation(UPDATE_SLIDE_SHOW);

  useEffect(() => () => {
    if (!savedRef.current) {
      setThemeIdStore(slideShow.themeId);
      return;
    }
    setSaved(false);
  }, []);

  const formattedThemes = themes.map(theme => ({
    label: theme.name,
    value: theme.id,
  }));

  const updateSlideShow = ({ title, desc, actType, hideDividerSlides, theme }) => {
    const mutationParams = {
      variables: {
        activityType: actType,
        description: desc,
        id: slideShow.id,
        showTransitionSlides: !hideDividerSlides,
        themeId: theme,
        title,
      },
    };

    setIsSaving(true);

    updateSlideShowMutation(mutationParams)
      .then((res) => {
        const { titleEn, titleEs, themeId, activityType, showTransitionSlides } = res.data?.updateSlideShow;
        const updatedThemeId = parseInt(themeId, 10);
        updateSlideShowInStore({ activityType, showTransitionSlides, themeId: updatedThemeId, titleEn, titleEs });
        setIsSaving(false);
        setSaved(true);
      })
      .catch((error) => {
        // eslint-disable-next-line no-console
        console.log(error);
        setIsSaving(false);
      });
  };

  const labelClass = 'tw-w-[9rem] tw-font-bold';

  return (
    <>
      <div className="tw-pt-1 tw-flex tw-items-center tw-justify-between">
        <div className="tw-font-bold">{I18n.t('edit_slide_show_settings')}</div>
        <div className="tw-flex tw-items-center tw-gap-2">
          {userType === 'Sysadmin' && (
            <a
              className="tw-flex tw-items-center tw-gap-1 btn btn--sm btn--outline-purple"
              href={admin_themes_path({ theme_id: storeThemeId })}
              rel="noopener noreferrer"
              target="_blank"
            >
              <Icon icon="EditPreview" size={16} />
              Edit CSS
            </a>
          )}
          <Popover.Close className="tw-outline-none tw-bg-transparent tw-border-none tw-cursor-pointer">
            <i aria-hidden="true" className="fa fa-times" />
          </Popover.Close>
        </div>
      </div>
      <hr />
      <Form
        initialValues={{
          actType: slideShow.activityType,
          desc: I18n.locale === 'es' ? slideShow.descriptionEs : slideShow.descriptionEn,
          hideDividerSlides: !slideShow.showTransitionSlides,
          theme: slideShow.themeId,
          title: I18n.locale === 'es' ? slideShow.titleEs : slideShow.titleEn,
        }}
        render={({ handleSubmit, hasValidationErrors, pristine }) => (
          <form onSubmit={handleSubmit}>
            <div className="tw-px-4">
              <TextField
                required
                label={I18n.t('title')}
                labelClass={labelClass}
                name="title"
                placeholder={I18n.t('enter_title')}
                validate={required}
              />
              <TextAreaField
                label={I18n.t('description')}
                labelClass={labelClass}
                name="desc"
                placeholder={`${I18n.t('enter_description')}...`}
              />

              {isAdmin && (
                <>
                  {/* Saving the options as hard coded for now while the gql epic is on going.
                  Should work on cleaning up to pull from the backend after to keep the options consistent */}
                  <SelectField
                    label={I18n.t('activity_type')}
                    labelClass={labelClass}
                    name="actType"
                    options={[
                      { label: I18n.t('classroom_activity'), value: 'classroom_activity' },
                      { label: I18n.t('video_activity'), value: 'video_activity' },
                      { label: I18n.t('investigation_activity'), value: 'investigation_activity' },
                      { label: I18n.t('lesson_outline'), value: 'lesson_outline' },
                      { label: I18n.t('professional_development'), value: 'professional_development' },
                      { label: I18n.t('marketing_slideshow'), value: 'marketing' },
                    ]}
                    placeholder={I18n.t('select_a_type')}
                  />
                  <SelectField
                    label={I18n.t('theme_id')}
                    labelClass={labelClass}
                    name="theme"
                    options={formattedThemes}
                    placeholder={I18n.t('select_a_theme')}
                    onChange={(e) => {
                      setThemeIdStore(e);
                    }}
                  />
                </>
              )}
              <CheckboxField
                className={`tw-m-0 ${styles.checkbox}`}
                label={I18n.t('hide_divider_slides')}
                labelClass={labelClass}
                name="hideDividerSlides"
                tooltipIcon="question"
                tooltipText={I18n.t('hide_divider_slides_tooltip')}
                onKeyDown={(e) => {
                  if (![' ', 'Enter'].includes(e.key)) return;

                  e.preventDefault();
                  e.target.click();
                }}
              />
            </div>

            <hr />
            <Footer
              primaryButtonDisabled={hasValidationErrors || pristine}
              primaryButtonText={I18n.t('save')}
              submitting={isSaving}
              submittingText={`${I18n.t('saving')}…`}
              wrapperClassName="tw-flex tw-justify-end tw-pb-2 tw-pr-2 tw-pt-0"
            />
          </form>
        )}
        onSubmit={updateSlideShow}
      />
    </>
  );
};

export default SlideShowTitleForm;
